import React, { useState, useEffect, useCallback, useRef } from "react";
import styles from "./View-ticket.module.scss";
import CustomTable from "../../../components/CustomTable";
import {
  UseDocumentUpdateContext,
  useDocumentListContext,
  useTicket,
} from "../../../utils/hooks";
import VisibilityIcon from "@mui/icons-material/Visibility";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import CustomSwitch from "../../../components/Switch";
import EllipsisText from "react-ellipsis-text";
import PropTypes from "prop-types";
import FileIcon from "../../../assets/Icons/FileIcon";
import MessageIcon from "../../../assets/Icons/MessageIcon";
import CheckBox from "../../../components/Checkbox";
import CustomButton from "../../../components/Button";
import { useParams, useLocation } from "react-router-dom";
import FilePreviewModal from "../../../components/FilePreviewModal";
import { toast } from "react-toastify";

const RESPONSE_CODES = {
  NEVER_INITIATED: 4018,
  IN_PROGRESS: 4019,
  COMPLETED: 4020,
  FAILED: 4021
};

const POLLING_INTERVAL = 30000; // 30 seconds

const Documentsrequested = React.memo(
  ({ handleFormData, readOnly, handleCheckboxData, ticketData }) => {
    const [showAllFiles, setShowAllFiles] = useState(false);
    const [gfrUploadStatus, setGfrUploadStatus] = useState(null);
    const [isStatusLoading, setIsStatusLoading] = useState(false);
    const [lastUploadedAt, setLastUploadedAt] = useState(null);
    const hasShownToastRef = useRef(false);
    const currentUploadIdRef = useRef(null);

    const { storeTableData, mutateDownloadZip, isDownloadZipLoading } =
      useDocumentListContext();
    const { fileViewerUrl, setFileViewerUrl } = UseDocumentUpdateContext();
    const {
      mutateUploadToGFR,
      mutateCheckGFRStatus,
    } = useTicket();

    const { id } = useParams();
    const location = useLocation();
    
    // Reset toast flag when ticket ID changes
    useEffect(() => {
      hasShownToastRef.current = false;
    }, [id]);

    // Status update function with localStorage sync and isInitialCheck flag
    const updateUploadStatus = useCallback((responseCode, uploadedAt, showToast = false, isInitialCheck = false) => {

      switch (responseCode) {
        case RESPONSE_CODES.COMPLETED:
          setGfrUploadStatus('completed');
          setLastUploadedAt(uploadedAt);
          // Save completed status to localStorage
          localStorage.setItem(`gfr_status_${id}`, 'completed');
          localStorage.setItem(`gfr_uploadedAt_${id}`, uploadedAt || '');

          if (showToast && !hasShownToastRef.current) {
            hasShownToastRef.current = true;
            toast.success('Questions successfully uploaded to GFR');
            // Clear the upload ID after successful completion
            localStorage.removeItem(`gfr_uploadId_${id}`);
          }
          break;

        case RESPONSE_CODES.FAILED:
          setGfrUploadStatus(null);
          setLastUploadedAt(null);
          // Clear localStorage on failure
          localStorage.removeItem(`gfr_status_${id}`);
          localStorage.removeItem(`gfr_uploadedAt_${id}`);

          if (showToast && !hasShownToastRef.current) {
            hasShownToastRef.current = true;
            toast.error('Failed to upload GFR');
          }
          break;

        case RESPONSE_CODES.IN_PROGRESS:
          if (!isInitialCheck) {
            // User initiated upload - show uploading state
            setGfrUploadStatus('uploading');
            setLastUploadedAt(null);
            localStorage.setItem(`gfr_status_${id}`, 'uploading');
            localStorage.removeItem(`gfr_uploadedAt_${id}`);
            hasShownToastRef.current = false;
          } else {
            setGfrUploadStatus(null); // Keep button as "Upload to GFR"
            setLastUploadedAt(null);
          }
          break;

        case RESPONSE_CODES.NEVER_INITIATED:
          setGfrUploadStatus(null);
          setLastUploadedAt(null);
          // Clear localStorage
          localStorage.removeItem(`gfr_status_${id}`);
          localStorage.removeItem(`gfr_uploadedAt_${id}`);

          if (showToast && !hasShownToastRef.current) {
            hasShownToastRef.current = true;
            toast.error('Please approve at least one question');
          }
          break;

        default:
          setGfrUploadStatus(null);
          setLastUploadedAt(null);
          // Clear localStorage on unknown status
          localStorage.removeItem(`gfr_status_${id}`);
          localStorage.removeItem(`gfr_uploadedAt_${id}`);
      }
    }, [id]);

    // Polling function with ticket status check
    const startPolling = useCallback(() => {
      const pollStatus = async () => {
        try {
          const statusResponse = await mutateCheckGFRStatus(id);
          const responseCode = statusResponse?.data?.responseCode;
          const uploadedAt = statusResponse?.data?.uploadedAt;

          updateUploadStatus(responseCode, uploadedAt, true);

          // Continue polling only if still in progress
          if (responseCode === RESPONSE_CODES.IN_PROGRESS) {
            setTimeout(pollStatus, POLLING_INTERVAL);
          }
        } catch (error) {
          console.error('Status check error:', error);
          // Continue polling on error
          setTimeout(pollStatus, POLLING_INTERVAL);
        }
      };

      // Start polling immediately, then continue with regular intervals
      pollStatus();
    }, [id, mutateCheckGFRStatus, updateUploadStatus]);

    // Check if there are questions (not documents) that can be uploaded to GFR
    const hasQuestions = useCallback((documents) => {
      if (!documents || !Array.isArray(documents)) return false;

      return documents.some(doc => {
        const hasQuestionText = doc?.question &&
          typeof doc.question === 'string' &&
          doc.question.trim() !== '';

        const isDocumentType = doc?.document_type &&
          typeof doc.document_type === 'string' &&
          doc.document_type.trim() !== '';

        // If it has a document_type, it's a document request, not a question
        if (isDocumentType) {
          return false;
        }

        // It's a question if it has question text and is not a document type
        return hasQuestionText;
      });
    }, []);

    // Initialize component state - always check server status on load
    useEffect(() => {
      const isTicketViewRoute = location.pathname.includes('/ticket/view/');

      if (!isTicketViewRoute || !id) {
        setIsStatusLoading(false);
        return;
      }

      // Always check server status on component load to get the latest upload time
      const checkInitialStatus = async () => {
        try {
          setIsStatusLoading(true);
          const statusResponse = await mutateCheckGFRStatus(id);
          const responseCode = statusResponse?.data?.responseCode;
          const uploadedAt = statusResponse?.data?.uploadedAt;

          updateUploadStatus(responseCode, uploadedAt, false, true);

          const savedStatus = localStorage.getItem(`gfr_status_${id}`);
          if (responseCode === RESPONSE_CODES.IN_PROGRESS && savedStatus === 'uploading') {
            startPolling();
          } else if (responseCode !== RESPONSE_CODES.IN_PROGRESS) {
            // Clear localStorage if upload is no longer in progress
            localStorage.removeItem(`gfr_status_${id}`);
            localStorage.removeItem(`gfr_uploadedAt_${id}`);
            localStorage.removeItem(`gfr_uploadId_${id}`);
            // Reset toast flag when clearing localStorage
            hasShownToastRef.current = false;
          }
        } catch (error) {
          console.error('Initial status check error:', error);
          console.error('Initial status check error:', error);
          // Fallback to localStorage if API fails
          const savedStatus = localStorage.getItem(`gfr_status_${id}`);
          const savedUploadedAt = localStorage.getItem(`gfr_uploadedAt_${id}`);

          if (savedStatus === 'completed' && savedUploadedAt) {
            setGfrUploadStatus('completed');
            setLastUploadedAt(savedUploadedAt);
          } else if (savedStatus === 'uploading') {
            setGfrUploadStatus('uploading');
          } else {
            setGfrUploadStatus(null);
            setLastUploadedAt(null);
          }
        } finally {
          setIsStatusLoading(false);
        }
      };

      checkInitialStatus();

    }, [id, location.pathname, mutateCheckGFRStatus, updateUploadStatus, startPolling]);

    // File handling functions
    const handleViewMoreClick = useCallback((value) => {
      setShowAllFiles((prev) => ({ ...prev, [value]: true }));
    }, []);

    const handleViewLessClick = useCallback((value) => {
      setShowAllFiles((prev) => ({ ...prev, [value]: false }));
    }, []);

    const handleDocumentView = useCallback((fileData) => {
      const { fileURL } = fileData;
      setFileViewerUrl(fileURL);
    }, [setFileViewerUrl]);

    const getSwitchLabel = useCallback((tableData, isEmpty) => {
      if (tableData.idrDocumentReceived || tableData.elsewhere || tableData.accepted) {
        return "Approved";
      } else if (isEmpty) {
        return "N/A";
      }
      return "Not Approved";
    }, []);

    const onDownloadClick = useCallback(() => {
      mutateDownloadZip(id);
    }, [mutateDownloadZip, id]);

    // Upload handler with proper status management
const handleUploadToGFR = useCallback(async () => {
  try {
    // Generate unique upload ID and reset toast flag for new upload
    const uploadId = Date.now().toString();
    currentUploadIdRef.current = uploadId;

    // CRITICAL: Always reset toast flag before any new upload attempt
    hasShownToastRef.current = false;

    // Force clear ALL localStorage entries related to this ticket
    localStorage.removeItem(`gfr_status_${id}`);
    localStorage.removeItem(`gfr_uploadedAt_${id}`);
    localStorage.removeItem(`gfr_uploadId_${id}`);
    localStorage.setItem(`gfr_uploadId_${id}`, uploadId);

    // For actual upload initiation, don't use isInitialCheck flag
    updateUploadStatus(RESPONSE_CODES.IN_PROGRESS, null, false, false);

    const uploadResponse = await mutateUploadToGFR(id);

    if (uploadResponse?.data?.success) {
      startPolling();
    } else {
      // Handle specific error response codes
      const responseCode = uploadResponse?.data?.responseCode;
      updateUploadStatus(responseCode, null, true, false);
    }
  } catch (error) {
    console.error('Upload error:', error);
    updateUploadStatus(RESPONSE_CODES.FAILED, null, false, false);
    toast.error('Failed to upload questions to GFR');
  }
}, [id, mutateUploadToGFR, startPolling, updateUploadStatus]);

    // Utility functions
    const hasDocuments = useCallback((documents) => {
      return documents?.some(doc => doc?.tickectIDRDocumentVersions?.length > 0);
    }, []);

    const formatUploadedDate = useCallback((dateString) => {
      if (!dateString) return '-';
      try {
        const date = new Date(dateString);
        return date.toLocaleString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          hour12: true
        });
      } catch (error) {
        return '-';
      }
    }, []);

    // Button text logic - simplified to only 3 states
    const getButtonText = () => {
      if (isStatusLoading) return 'Loading...';
      if (gfrUploadStatus === 'uploading') return 'Uploading in Progress';
      return 'Upload to GFR'; // Default for null, completed, and failed states
    };

    // Updated button disabled logic to include hasQuestions check
    const isButtonDisabled = gfrUploadStatus === 'uploading' ||
      ticketData?.isPurged ||
      isStatusLoading ||
      !hasQuestions(storeTableData);

    const columns = [
      {
        name: "document_type",
        label: "Request Name",
        options: {
          filter: false,
          sort: false,
          customBodyRenderLite: (value) => {
            const data = storeTableData[value];
            return (
              <div className={styles.documentType}>
                <div className={styles.documentTypeIcon}>
                  {data.document_type ? <FileIcon /> : <MessageIcon />}
                </div>
                <p>{data.document_type || data.question}</p>
              </div>
            );
          },
          setCellHeaderProps: () => ({
            style: {
              fontSize: "12px",
              background: "#1D1D1D05",
              borderBottom: "0",
              textAlign: "left",
            },
          }),
          setCellProps: () => ({
            style: {
              left: "0",
              background: "#ffffff",
              textAlign: "center",
              zIndex: 100,
              color: "#4A4A4A",
              padding: "0!important",
              textTransform: "capitalize",
              borderBottom: "1px solid #CCCCCC",
            },
          }),
        },
      },
      {
        name: "documents",
        label: "Uploaded Document",
        options: {
          filter: false,
          sort: false,
          customBodyRenderLite: (value) => {
            const data = storeTableData[value];
            if (data.question) {
              return (
                <div className={styles.requestAnswer}>
                  {data.answers || "Answer not provided"}
                </div>
              );
            }
            return (
              <ul
                className={styles.fileInputWrapper}
                style={
                  data.tickectIDRDocumentVersions.length <= 2
                    ? { height: "auto" }
                    : { height: "170px" }
                }
              >
                {(showAllFiles[value] ? data.tickectIDRDocumentVersions : data.tickectIDRDocumentVersions.slice(0, 3))
                  .map((item) => (
                    <li key={item?.fileURL}>
                      {item?.fileReference && (
                        <EllipsisText
                          text={item.fileReference}
                          length={30}
                          tooltip={item.fileReference}
                        />
                      )}
                      {!ticketData?.isPurged && (
                        <VisibilityIcon
                          sx={{
                            fontSize: "15px",
                            cursor: "pointer",
                          }}
                          onClick={() => handleDocumentView(item)}
                        />
                      )}
                    </li>
                  ))}
                {data.tickectIDRDocumentVersions.length === 0 && (
                  <li>No Documents Uploaded.</li>
                )}
                {data.tickectIDRDocumentVersions.length > 3 && !showAllFiles[value] && (
                  <li
                    onClick={() => handleViewMoreClick(value)}
                    onKeyDown={() => { }}
                    className={styles.viewMoreData}
                  >
                    <p>
                      View More
                      <UnfoldMoreIcon sx={{ fontSize: "18px" }} />
                    </p>
                  </li>
                )}
                {showAllFiles[value] && (
                  <li
                    onClick={() => handleViewLessClick(value)}
                    onKeyDown={() => { }}
                    className={styles.viewMoreData}
                  >
                    <p>
                      View Less
                      <UnfoldMoreIcon sx={{ fontSize: "18px" }} />
                    </p>
                  </li>
                )}
              </ul>
            );
          },
          setCellProps: () => ({
            style: {
              left: "0",
              background: "#ffffff",
              zIndex: 100,
              padding: "0!important",
              textTransform: "capitalize",
              textAlign: "left",
              borderBottom: "1px solid #CCCCCC",
            },
          }),
          setCellHeaderProps: () => ({
            style: {
              fontSize: "12px",
              background: "#1D1D1D05",
              borderBottom: "0",
              textAlign: "left",
            },
          }),
        },
      },
      {
        name: "elsewhere",
        label: "Received Elsewhere",
        options: {
          filter: false,
          sort: false,
          customBodyRenderLite: (value) => {
            const data = storeTableData[value];
            const isDisabled =
              ((data?.tickectIDRDocumentVersions?.length ?? 0) > 0) ||
              (!data?.tickectIDRDocumentVersions && data?.answers) ||
              readOnly;

            return (
              <div style={{ display: "flex", justifyContent: "center" }}>
                <CheckBox
                  disabled={isDisabled}
                  checked={data?.elsewhere || false}
                  onChange={(e) => handleCheckboxData(data, e.target.checked)}
                />
              </div>
            );
          },
          setCellProps: () => ({
            style: {
              left: "0",
              background: "#ffffff",
              zIndex: 100,
              padding: "0!important",
              textTransform: "capitalize",
              textAlign: "left",
              borderBottom: "1px solid #CCCCCC",
            },
          }),
          setCellHeaderProps: () => ({
            style: {
              fontSize: "12px",
              background: "#1D1D1D05",
              borderBottom: "0",
              textAlign: "center",
            },
          }),
        },
      },
      {
        name: "actions",
        label: "Actions",
        options: {
          filter: false,
          sort: false,
          customBodyRenderLite: (value) => {
            const tableData = storeTableData[value];
            const isEmpty = tableData.elsewhere || tableData?.tickectIDRDocumentVersions?.length === 0;
            const isNotAnswered = tableData?.questionID && !tableData?.answered;
            const isDisabled = isEmpty || readOnly || isNotAnswered;

            return (
              <CustomSwitch
                disabled={isDisabled}
                checked={tableData.idrDocumentReceived || tableData.accepted || false}
                switchLabel={getSwitchLabel(tableData, isEmpty || isNotAnswered)}
                handleSwitch={(e) => handleFormData(tableData, e.target.checked)}
              />
            );
          },
          setCellProps: () => ({
            style: {
              left: "0",
              background: "#ffffff",
              zIndex: 100,
              padding: "0!important",
              textTransform: "capitalize",
              textAlign: "center",
              borderBottom: "1px solid #CCCCCC",
            },
          }),
          setCellHeaderProps: () => ({
            style: {
              fontSize: "12px",
              textAlign: "center",
              background: "#1D1D1D05",
              borderBottom: "0",
            },
          }),
        },
      },
    ];

    return (
      <div className={styles.dataTableWrapper}>
        <div className={styles.document_list_table}>
          <div className={styles.document_list_header}>
            <h5>Documents Requested</h5>
            <CustomButton
              disabled={
                isDownloadZipLoading ||
                !hasDocuments(storeTableData) ||
                ticketData?.isPurged
              }
              onClick={onDownloadClick}
            >
              Download Documents
            </CustomButton>
          </div>
          <CustomTable
            data={storeTableData || []}
            columns={columns}
            noDataText="No Documents Requested"
          />
          <div style={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            marginTop: '10px',
            gap: '10px'
          }}>
            <span style={{ fontSize: '12px', color: '#666', whiteSpace: 'nowrap' }}>
              Last uploaded: {(gfrUploadStatus === 'completed' && lastUploadedAt)
                ? formatUploadedDate(lastUploadedAt)
                : '-'}
            </span>
            <div style={{ minWidth: '180px' }}>
              <CustomButton
                disabled={isButtonDisabled}
                onClick={handleUploadToGFR}
                style={{ float: 'right' }}
              >
                {getButtonText()}
              </CustomButton>
            </div>
          </div>
        </div>
        {fileViewerUrl && (
          <FilePreviewModal
            open={!!fileViewerUrl}
            fileData={fileViewerUrl}
            handleClose={() => setFileViewerUrl(undefined)}
          />
        )}
      </div>
    );
  }
);

Documentsrequested.propTypes = {
  handleFormData: PropTypes.func,
  handleCheckboxData: PropTypes.func,
  readOnly: PropTypes.bool,
  ticketData: PropTypes.object,
};

export default Documentsrequested;